// Single source of truth for localized URLs (the /software/** docs plus the
// marketing pages that have a translated slug, e.g. /contatos, /planos-e-precos).
//
// The Pages Router serves every locale from the same Portuguese-named file
// route (e.g. pages/software/planeamento/criar-o-plano-de-compras.tsx), so
// without this table, visiting the French version of a doc page produced a
// Portuguese-worded URL (e.g. /fr/software/planeamento/criar-o-plano-de-compras).
// Google treats a non-French URL as a negative signal for a French page, so
// each non-pt locale gets its own translated path here. next.config.ts turns
// this into rewrites (pretty URL -> real file route) and redirects (old
// pt-worded URL -> pretty URL, so already-indexed links keep their value).
//
// The actual data lives in localizedRoutes.json (not here) so that plain
// Node scripts that can't load .ts files -- namely next-sitemap.js, which
// runs as a standalone CLI after build -- can require() it directly too.
import routes from "./localizedRoutes.json";

export type LocalizedRouteEntry = {
  pt: string;
  en: string;
  es: string;
  fr: string;
};

export const LOCALIZED_ROUTES: LocalizedRouteEntry[] = routes;

export type LocalizableLocale = "en" | "es" | "fr";

const isLocalizable = (locale?: string): locale is LocalizableLocale =>
  locale === "en" || locale === "es" || locale === "fr";

// Returns the pretty, locale-worded URL for a pt-worded path (as found in
// router.pathname / page component "to" fields). Returns the path unchanged
// for pt, for locales outside the table, and for paths with no entry in it.
export const localizePath = (ptPath: string, locale?: string): string => {
  if (!isLocalizable(locale)) return ptPath;
  const entry = LOCALIZED_ROUTES.find((route) => route.pt === ptPath);
  return entry ? entry[locale] : ptPath;
};

// True when this pt-worded route has translated slugs in the table. Callers
// that rebuild a URL for a different locale need this to tell "no entry, keep
// the path as-is" apart from "entry exists, but pt's slug is the path itself".
export const hasLocalizedRoute = (ptPath: string): boolean =>
  LOCALIZED_ROUTES.some((route) => route.pt === ptPath);
